import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table2 } from 'lucide-react'

import routes from 'constants/routes'
import { cn } from 'lib/utils'

import { templateDraftPath } from '../constants'
import type { ExplorerScopeType } from '../types/explorer.types'
import { ExplorerTemplateGallery } from './ExplorerTemplateGallery'

interface AssetReportsButtonProps {
	type: ExplorerScopeType
	/** The asset's id; the button renders nothing until it is known. */
	entityId: string | undefined
	/** The asset's name, for the gallery's title. */
	name: string | undefined
	className?: string
}

/**
 * "Reports" on an asset's page (MYD-598).
 *
 * Opens the template gallery bound to this asset: only templates that fit it
 * are offered, and choosing one opens it in Explorer narrowed to this asset, as
 * an unsaved draft. Styled as the pill next to "Presentations" so the two read
 * as one row of asset-level outputs.
 */
export function AssetReportsButton({ type, entityId, name, className }: AssetReportsButtonProps) {
	const navigate = useNavigate()
	const [open, setOpen] = useState(false)

	if (!entityId) return null

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className={cn(
					'inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50',
					className,
				)}
			>
				<Table2 className="h-3.5 w-3.5 text-primary" />
				Reports
			</button>
			<ExplorerTemplateGallery
				open={open}
				onClose={() => setOpen(false)}
				boundAsset={{ type, id: entityId, name: name || `this ${type}` }}
				onUse={(template, scope) => {
					setOpen(false)
					navigate(templateDraftPath(routes.EXPLORER, template, scope))
				}}
			/>
		</>
	)
}

export default AssetReportsButton
